import { SimpleSearchFilter } from 'api/dashboard/widget';
import { IntegrationConfig } from '../types';

export type SalesforceIntegrationId = { integrationId: string | 'new' };

export type SalesforceIntegrationProvider = {
  authType: string;
  oauthUrl: string;
  oauthSandboxUrl: string;
  oauthReauthUrl: string;
  marketplaceAppUrl: string;
  marketplaceAppInstallationRequired?: boolean;
};

export enum SalesforceActions {
  Project = 'project',
  Task = 'task',
}

export type SalesforceObject = {
  id: string;
  name: string;
  url?: string;
  type?: string;
};

export enum AutomationOperation {
  Upsert = 'UPSERT',
}

export type Condition = {
  name: string;
  value: string;
  operation: string;
};

export enum IntegrationSource {
  NativeIntegration = 'NATIVE_INTEGRATION',
}

export type SalesforceAutomationCondition = Omit<
  SimpleSearchFilter,
  'customFields'
>;

export type SalesforceAutomationAction = {
  integrationType: string;
  resourceType: SalesforceActions;
  operation: AutomationOperation;
  inputs: Record<string, unknown>;
};

export type SalesforceOrderPayload = {
  automationId: number;
  automationUpdatedOrder: number;
};

export type SalesforceAutomation = {
  actions: SalesforceAutomationAction[];
  automationId?: number;
  automationName?: string;
  description?: string;
  createdAt: string;
  order: number;
  integrationType: string;
  enabled: boolean;
  matchingCondition?: SalesforceAutomationCondition;
  object?: string;
  triggerCondition: SalesforceAutomationCondition;
  source: IntegrationSource;
  sourceId: number;
  updatedAt: string;
};

export type CreateAutomationPayload = Omit<
  SalesforceAutomation,
  'automationId' | 'createdAt' | 'deleted' | 'order' | 'updatedAt'
>;

export type UpdateAutomationPayload = Omit<
  SalesforceAutomation,
  'createdAt' | 'deleted' | 'order' | 'updatedAt'
>;

export type SalesforceConfig = IntegrationConfig<{
  externalAccountId: string;
}>;

export enum SalesforceSyncTypes {
  SyncTwoWay = 'SYNC_TWO_WAY',
  SyncNativeToExternal = 'SYNC_NATIVE_TO_EXTERNAL',
  SyncExternalToNative = 'SYNC_EXTERNAL_TO_NATIVE',
  None = 'NONE',
}

export type SalesforceFieldMapping = {
  fieldMappingConfigId?: number;
  integrationId: number;
  externalIntegrationType: 'salesforce'; // consciously hardcoded. Providing options doesn't make sense for a fixed value
  resouceMappingConfigId: number;
  nativeFieldId: number;
  nativeFieldName?: string;
  nativeFieldLabel: string;
  externalFieldId: string;
  externalFieldName: string;
  syncType: SalesforceSyncTypes;
  enabled?: boolean;
  allowedSyncTypes: [SalesforceSyncTypes];
};

export type SalesforceResourceMapping = {
  resourceMappingConfigId: number;
  integrationId: number;
  externalIntegrationType: 'salesforce'; // consciously hardcoded. Providing options doesn't make sense for a fixed value
  nativeResourceType: string;
  externalResourceType: string;
  syncType: SalesforceSyncTypes;
  fieldMappings: SalesforceFieldMapping[];
  enabled?: boolean;
};

export type SalesforceResourceCreatePayload = AtLeast<
  SalesforceResourceMapping,
  | 'externalIntegrationType'
  | 'externalResourceType'
  | 'nativeResourceType'
  | 'syncType'
  | 'enabled'
>;

// TODO: @krishna Remove this once the error codes are standardised
export enum AutomationErrorCodes {
  ConflictingFieldValue = 'CONFLICTING_FIELD_VALUE',
}

export type AutomationError = {
  code: AutomationErrorCodes;
  reason: string;
};

export interface SfInstallations {
  installationId: number;
  externalInstallationId: string;
  externalIntegrationType: string;
  externalAccountId: string;
  installedAppId: string;
  installedAppName: string;
  installedAppVersionId: string;
  installedAppVersionName: string;
  // TODO: change this to enum
  status: string;
}
